import { useMemo, useState } from 'react';
import { Alert, Button, Card, Field, Input } from '@busbook/ui';
import { Page } from '../components/Shell.js';
import { Column, DataTable, Drawer } from '../components/DataTable.js';
import { errorMessage, useResource } from '../lib/useResource.js';
import { api } from '../lib/api.js';
import { useSession } from '../lib/session.js';
import { useT } from '../i18n/index.js';

interface Stop {
  id: string;
  name: string;
  city: string;
  address?: string | null;
  active: boolean;
}

interface RouteStop {
  id: string;
  stopId: string;
  position: number;
  arrivalOffsetMin: number;
  stop: Stop;
}

interface Route {
  id: string;
  name: string;
  active: boolean;
  routeStops: RouteStop[];
  _count: { timetables: number };
}

interface Fare {
  id: string;
  fromStopId: string;
  toStopId: string;
  priceCents: number;
}

interface DraftStop {
  stopId: string;
  arrivalOffsetMin: number;
}

export function Network() {
  const t = useT();
  const { tenant } = useSession();
  const stops = useResource<Stop[]>('/admin/stops');
  const routes = useResource<Route[]>('/admin/routes');

  const [stopForm, setStopForm] = useState<Partial<Stop> | null>(null);
  const [routeForm, setRouteForm] = useState<{ id?: string; name: string; stops: DraftStop[] } | null>(null);
  const [fareRouteId, setFareRouteId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const stopColumns: Column<Stop>[] = [
    { key: 'name', header: t('f.stop'), render: (s) => <span className="font-medium">{s.name}</span> },
    { key: 'city', header: t('f.city'), render: (s) => s.city },
    { key: 'address', header: t('f.address'), render: (s) => s.address || '—' },
    {
      key: 'active',
      header: t('f.status'),
      render: (s) => (
        <span style={{ color: s.active ? 'var(--signal-departed)' : 'var(--color-faint)' }}>
          {s.active ? 'Active' : 'Hidden'}
        </span>
      ),
    },
  ];

  const routeColumns: Column<Route>[] = [
    { key: 'name', header: t('f.route'), render: (r) => <span className="font-medium">{r.name}</span> },
    {
      key: 'path',
      header: t('f.stops'),
      render: (r) => (
        <span style={{ color: 'var(--color-muted)' }}>
          {r.routeStops.map((rs) => rs.stop.name).join(' → ')}
        </span>
      ),
    },
    {
      key: 'duration',
      header: t('f.duration'),
      numeric: true,
      render: (r) => {
        const minutes = r.routeStops.at(-1)?.arrivalOffsetMin ?? 0;
        return `${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, '0')}m`;
      },
    },
    { key: 'timetables', header: t('f.timetables'), numeric: true, render: (r) => r._count.timetables },
  ];

  async function saveStop() {
    if (!stopForm) return;
    setSaving(true);
    setError(null);
    try {
      const payload = {
        name: stopForm.name ?? '',
        city: stopForm.city ?? '',
        address: stopForm.address || undefined,
        active: stopForm.active ?? true,
      };
      if (stopForm.id) await api.put(`/admin/stops/${stopForm.id}`, payload);
      else await api.post('/admin/stops', payload);
      setStopForm(null);
      await stops.reload();
    } catch (err) {
      setError(errorMessage(err, 'Could not save this stop.'));
    } finally {
      setSaving(false);
    }
  }

  async function saveRoute() {
    if (!routeForm) return;
    setSaving(true);
    setError(null);
    try {
      const payload = { name: routeForm.name, active: true, stops: routeForm.stops };
      if (routeForm.id) await api.put(`/admin/routes/${routeForm.id}`, payload);
      else await api.post('/admin/routes', payload);
      setRouteForm(null);
      await routes.reload();
    } catch (err) {
      setError(errorMessage(err, 'Could not save this route.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Page
      title={t('nav.network')}
      description={t('nw.subtitle')}
      actions={
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => setStopForm({ active: true })}>
            {t('nw.addStop')}
          </Button>
          <Button
            onClick={() => setRouteForm({ name: '', stops: [{ stopId: '', arrivalOffsetMin: 0 }, { stopId: '', arrivalOffsetMin: 60 }] })}
            disabled={(stops.data?.length ?? 0) < 2}
          >
            {t('nw.newRoute')}
          </Button>
        </div>
      }
    >
      <section>
        <h2 className="mb-2 text-sm font-semibold">{t('f.routes')}</h2>
        <DataTable
          columns={routeColumns}
          rows={routes.data}
          rowKey={(route) => route.id}
          loading={routes.loading}
          error={routes.error}
          onRowClick={(route) =>
            setRouteForm({
              id: route.id,
              name: route.name,
              stops: route.routeStops.map((rs) => ({
                stopId: rs.stopId,
                arrivalOffsetMin: rs.arrivalOffsetMin,
              })),
            })
          }
          empty={{
            title: t('nw.noRoutes'),
            description:
              (stops.data?.length ?? 0) < 2
                ? 'Add at least two stops, then connect them into a route.'
                : 'A route is an ordered list of stops with the minutes between them.',
          }}
        />
        {routes.data && routes.data.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {routes.data.map((route) => (
              <Button key={route.id} size="sm" variant="secondary" onClick={() => setFareRouteId(route.id)}>
                Fares for {route.name}
              </Button>
            ))}
          </div>
        )}
      </section>

      <section className="mt-9">
        <h2 className="mb-2 text-sm font-semibold">{t('f.stops')}</h2>
        <DataTable
          columns={stopColumns}
          rows={stops.data}
          rowKey={(stop) => stop.id}
          loading={stops.loading}
          error={stops.error}
          onRowClick={(stop) => setStopForm(stop)}
          empty={{ title: t('nw.noStops'), description: t('nw.addStopsHint') }}
        />
      </section>

      <Drawer
        open={stopForm !== null}
        title={stopForm?.id ? 'Edit stop' : 'Add stop'}
        onClose={() => {
          setStopForm(null);
          setError(null);
        }}
        footer={
          <>
            <Button variant="secondary" onClick={() => setStopForm(null)}>
              {t('f.cancel')}
            </Button>
            <Button onClick={saveStop} loading={saving}>
              {t('nw.saveStop')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {error && <Alert tone="error">{error}</Alert>}
          <Field label={t('f.name')} htmlFor="stop-name" hint={t('nw.nameHint')}>
            <Input
              id="stop-name"
              value={stopForm?.name ?? ''}
              onChange={(e) => setStopForm({ ...stopForm, name: e.target.value })}
            />
          </Field>
          <Field label={t('f.city')} htmlFor="stop-city">
            <Input
              id="stop-city"
              value={stopForm?.city ?? ''}
              onChange={(e) => setStopForm({ ...stopForm, city: e.target.value })}
            />
          </Field>
          <Field label={t('f.address')} htmlFor="stop-address" hint={t('nw.addrHint')}>
            <Input
              id="stop-address"
              value={stopForm?.address ?? ''}
              onChange={(e) => setStopForm({ ...stopForm, address: e.target.value })}
            />
          </Field>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={stopForm?.active ?? true}
              onChange={(e) => setStopForm({ ...stopForm, active: e.target.checked })}
            />
            {t('nw.showToPax')}
          </label>
        </div>
      </Drawer>

      <Drawer
        open={routeForm !== null}
        title={routeForm?.id ? 'Edit route' : 'New route'}
        onClose={() => {
          setRouteForm(null);
          setError(null);
        }}
        footer={
          <>
            <Button variant="secondary" onClick={() => setRouteForm(null)}>
              {t('f.cancel')}
            </Button>
            <Button onClick={saveRoute} loading={saving}>
              {t('nw.saveRoute')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {error && <Alert tone="error">{error}</Alert>}
          <Field label={t('nw.routeName')} htmlFor="route-name" hint={t('nw.routeHint')}>
            <Input
              id="route-name"
              value={routeForm?.name ?? ''}
              onChange={(e) => setRouteForm(routeForm ? { ...routeForm, name: e.target.value } : null)}
            />
          </Field>

          <div>
            <p className="mb-1.5 text-sm font-medium">{t('nw.stopsInOrder')}</p>
            <p className="mb-2 text-xs" style={{ color: 'var(--color-muted)' }}>
              Minutes are counted from leaving the first stop, so the first is always 0.
            </p>
            <ul className="flex flex-col gap-2">
              {(routeForm?.stops ?? []).map((draft, index) => (
                <li key={index} className="flex items-center gap-2">
                  <span className="w-5 text-xs tabular" style={{ color: 'var(--color-faint)' }}>
                    {index + 1}
                  </span>
                  <select
                    aria-label={`Stop ${index + 1}`}
                    className="h-10 flex-1 rounded-lg border px-2 text-sm"
                    style={{ borderColor: 'var(--color-line-strong)', background: 'var(--color-bg)' }}
                    value={draft.stopId}
                    onChange={(e) =>
                      setRouteForm(
                        routeForm
                          ? {
                              ...routeForm,
                              stops: routeForm.stops.map((s, i) =>
                                i === index ? { ...s, stopId: e.target.value } : s,
                              ),
                            }
                          : null,
                      )
                    }
                  >
                    <option value="">{t('nw.chooseStop')}</option>
                    {(stops.data ?? []).map((stop) => (
                      <option key={stop.id} value={stop.id}>
                        {stop.name} · {stop.city}
                      </option>
                    ))}
                  </select>
                  <input
                    aria-label={`Minutes from departure for stop ${index + 1}`}
                    type="number"
                    min={0}
                    disabled={index === 0}
                    className="h-10 w-24 rounded-lg border px-2 text-sm tabular disabled:opacity-50"
                    style={{ borderColor: 'var(--color-line-strong)', background: 'var(--color-bg)' }}
                    value={draft.arrivalOffsetMin}
                    onChange={(e) =>
                      setRouteForm(
                        routeForm
                          ? {
                              ...routeForm,
                              stops: routeForm.stops.map((s, i) =>
                                i === index ? { ...s, arrivalOffsetMin: Number(e.target.value) } : s,
                              ),
                            }
                          : null,
                      )
                    }
                  />
                  <button
                    type="button"
                    aria-label={`Remove stop ${index + 1}`}
                    disabled={(routeForm?.stops.length ?? 0) <= 2}
                    onClick={() =>
                      setRouteForm(
                        routeForm
                          ? { ...routeForm, stops: routeForm.stops.filter((_, i) => i !== index) }
                          : null,
                      )
                    }
                    className="px-2 text-lg leading-none disabled:opacity-25"
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
            <Button
              size="sm"
              variant="secondary"
              className="mt-2"
              onClick={() =>
                setRouteForm(
                  routeForm
                    ? {
                        ...routeForm,
                        stops: [
                          ...routeForm.stops,
                          {
                            stopId: '',
                            arrivalOffsetMin: (routeForm.stops.at(-1)?.arrivalOffsetMin ?? 0) + 30,
                          },
                        ],
                      }
                    : null,
                )
              }
            >
              {t('nw.addStop')}
            </Button>
          </div>
        </div>
      </Drawer>

      {fareRouteId && (
        <FareDrawer
          routeId={fareRouteId}
          currency={tenant?.currency ?? 'EUR'}
          onClose={() => setFareRouteId(null)}
        />
      )}
    </Page>
  );
}

/**
 * Fare matrix: the upper triangle of from-stop × to-stop, because a passenger can
 * only travel forwards along a route (docs/06 "/network").
 */
function FareDrawer({
  routeId,
  currency,
  onClose,
}: {
  routeId: string;
  currency: string;
  onClose: () => void;
}) {
  const t = useT();
  const resource = useResource<{ stops: RouteStop[]; fares: Fare[] }>(`/admin/routes/${routeId}/fares`);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const stops = resource.data?.stops ?? [];
  const priceByPair = useMemo(() => {
    const map: Record<string, string> = {};
    for (const fare of resource.data?.fares ?? []) {
      map[`${fare.fromStopId}:${fare.toStopId}`] = (fare.priceCents / 100).toFixed(2);
    }
    return map;
  }, [resource.data]);

  const value = (from: string, to: string) => draft[`${from}:${to}`] ?? priceByPair[`${from}:${to}`] ?? '';

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const merged = { ...priceByPair, ...draft };
      const fares = Object.entries(merged)
        .filter(([, price]) => price !== '' && Number.isFinite(Number(price)))
        .map(([pair, price]) => {
          const [fromStopId, toStopId] = pair.split(':') as [string, string];
          return { fromStopId, toStopId, priceCents: Math.round(Number(price) * 100) };
        });
      await api.put(`/admin/routes/${routeId}/fares`, { fares });
      onClose();
    } catch (err) {
      setError(errorMessage(err, 'Could not save these fares.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Drawer
      open
      title={t('f.fares')}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('f.cancel')}
          </Button>
          <Button onClick={save} loading={saving}>
            {t('nw.saveFares')}
          </Button>
        </>
      }
    >
      {error && (
        <div className="mb-3">
          <Alert tone="error">{error}</Alert>
        </div>
      )}
      <Card>
        <p className="text-sm" style={{ color: 'var(--color-muted)' }}>
          Price per segment in {currency}. Leave a cell blank if that journey is not sold.
        </p>
        <div className="mt-3 overflow-x-auto">
          <table className="border-collapse text-sm">
            <thead>
              <tr>
                <th className="px-2 py-1 text-left text-xs" style={{ color: 'var(--color-faint)' }}>
                  {t('nw.fromTo')}
                </th>
                {stops.slice(1).map((to) => (
                  <th key={to.id} className="px-2 py-1 text-left text-xs" style={{ color: 'var(--color-faint)' }}>
                    {to.stop.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {stops.slice(0, -1).map((from, rowIndex) => (
                <tr key={from.id}>
                  <th className="whitespace-nowrap px-2 py-1 text-left text-xs font-medium">
                    {from.stop.name}
                  </th>
                  {stops.slice(1).map((to, colIndex) => (
                    <td key={to.id} className="px-1 py-1">
                      {colIndex >= rowIndex ? (
                        <input
                          aria-label={`Fare from ${from.stop.name} to ${to.stop.name}`}
                          type="number"
                          min={0}
                          step="0.01"
                          className="h-9 w-24 rounded border px-2 text-sm tabular"
                          style={{ borderColor: 'var(--color-line-strong)', background: 'var(--color-bg)' }}
                          value={value(from.stopId, to.stopId)}
                          onChange={(e) =>
                            setDraft({ ...draft, [`${from.stopId}:${to.stopId}`]: e.target.value })
                          }
                        />
                      ) : (
                        <span className="block h-9 w-24" aria-hidden />
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </Drawer>
  );
}
